import { GoogleAnalyticsProvider } from "./GoogleAnalyticsProvider";
import { CapacitorGoogleAnalyticsProvider } from "./CapacitorGoogleAnalyticsProvider";

import {
	EngagementTrackingEnum,
	EventTrackingEnum,
	UserActionTypeEnum,
	UserTypeEnum,
	SearchTrackingEnum,
	LiveEventTypeTrackingEnum,
} from "@data-types/analytics-tracker/AnalyticsTrackerEnum";
import {
	UserActionTrackingType,
	EngagementTrackingType,
	EventTrackingType,
	BetPlacedMatchType,
	FirstLoginModel,
	RegistrationCampaignSubmitModel,
	RegistrationInteractionModel,
} from "@data-types/analytics-tracker";

import { isCapacitorPlatform } from "@utils/specific/capacitor/helpers";
import { XtremePushProvider } from "./XtremePushProvider";
import { CapacitorXtremePushProvider } from "./CapacitorXtremePushProvider";
import { BingUETProvider } from "./BingUETProvider";
import { MetaPixelProvider } from "./MetaPixelProvider";
import { LoginResponseUser } from "@api-types/user/LoginResponseDto";
import { RegistrationStepsEnum } from "@data-types/analytics-tracker/RegistrationStepsEnum";
import { RegistrationInteractionTypeEnum } from "@data-types/analytics-tracker/RegistrationInteractionTypeEnum";
import { SearchAction } from "@state/stores/offer/search/SearchStore";
import {
	GA_REGISTRATION_TRACKING_REQUIRES_CONSENT,
	isAnalyticsConsentSatisfied,
} from "./cookieConsentConfig";
import {
	getRegistrationCampaign,
	getRegistrationSessionId,
} from "./registrationCampaignStorage";

export type DataType =
	UserActionTrackingType | EngagementTrackingType | EventTrackingType;

class AnalyticsTracker {
	private googleAnalyticsProvider:
		GoogleAnalyticsProvider | CapacitorGoogleAnalyticsProvider;

	private xtremePushProvider:
		XtremePushProvider | CapacitorXtremePushProvider;

	private bingUETProvider: BingUETProvider | null = null;
	private metaPixelProvider: MetaPixelProvider | null = null;

	public userData: LoginResponseUser | null = null;

	utmParams: {
		utm_source: string;
		utm_medium: string;
		utm_campaign: string;
		utm_term: string;
		utm_content: string;
	};

	constructor() {
		if (isCapacitorPlatform()) {
			this.googleAnalyticsProvider =
				new CapacitorGoogleAnalyticsProvider();
			this.xtremePushProvider = new CapacitorXtremePushProvider();
		} else {
			this.googleAnalyticsProvider = new GoogleAnalyticsProvider();
			this.xtremePushProvider = new XtremePushProvider();
			this.bingUETProvider = new BingUETProvider();
			this.metaPixelProvider = new MetaPixelProvider();
		}
	}

	public setUserData(userData: LoginResponseUser) {
		this.userData = userData;
	}

	// used on pages that will have utm parameters
	getUTMParameters() {
		const cleanValue = (value: string | null, fallback: string = "") => {
			if (!value) return fallback;

			try {
				return JSON.parse(value);
			} catch {
				return value;
			}
		};
		const searchParams = new URLSearchParams(window.location.search);

		this.utmParams = {
			utm_source: cleanValue(searchParams.get("utm_source"), "Direct"),
			utm_medium: cleanValue(searchParams.get("utm_medium")),
			utm_campaign: cleanValue(searchParams.get("utm_campaign")),
			utm_term: cleanValue(searchParams.get("utm_term")),
			utm_content: cleanValue(searchParams.get("utm_content")),
		};
	}

	//#region page views
	trackPageView(url: string) {
		const urlWithoutCulture = url.split("/").slice(2).join("/");

		if (
			this.googleAnalyticsProvider instanceof
			CapacitorGoogleAnalyticsProvider
		) {
			this.googleAnalyticsProvider.trackPageView(
				UserActionTypeEnum.PageViewed,
				urlWithoutCulture
			);
		}
	}

	//#region betting
	// don't know which parameters will be needed, change when needed
	trackBetSlipView(
		betId: string,
		betSlipStatus: string,
		payment: number,
		maxGain: number,
		betStatusType: string,
		betSlipNumber: string
	) {
		const data: DataType = {
			category: "Betting",
			bet_id: betId,
			bet_slip_status_abrv: betSlipStatus,
			payment: payment.toString(),
			max_gain: maxGain.toString(),
			bet_status_abrv: betStatusType,
			bet_slip_number: betSlipNumber,
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.BetSlipViewed,
			data
		);
	}

	trackBetPlaced(
		betSlipType: string,
		betType: string,
		payment: number,
		betId: string,
		matches: BetPlacedMatchType[],
		maxGain: number
	) {
		const data: DataType = {
			category: "Betting",
			bet_slip_type: betSlipType,
			bet_type: betType,
			matches: matches,
			payment: payment.toString(),
			bet_id: betId,
			max_gain: maxGain.toString(),
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.BetPlaced,
			data
		);
		if (this.userData) {
			this.xtremePushProvider.trackEvent(
				UserActionTypeEnum.BetPlaced,
				data
			);
		}
	}

	trackBetSlipInteraction(type: string) {
		const data: DataType = {
			category: "Betting",
			interaction_label: type,
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.BetSlipInteraction,
			data
		);
	}

	trackBetSlipCancelInteraction(type: string) {
		const data: DataType = {
			category: "Betting",
			interaction_label: type,
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.BetSlipCancelInteraction,
			data
		);
	}

	trackBetSlipSharePopupAdd(type: string) {
		const data: DataType = {
			category: "Betting",
			interaction_label: type,
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.BetSlipSharePopupAdd,
			data
		);
	}

	trackCashOutRequested(
		cashoutLocation: string | null,
		calculatedAmount: number
	) {
		const data: DataType = {
			category: "Betting",
			cash_out_location: cashoutLocation,
			calculated_amount: calculatedAmount.toString(),
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.CashOutRequested,
			data
		);

		if (this.userData) {
			this.xtremePushProvider.trackEvent(
				UserActionTypeEnum.CashOutRequested,
				data
			);
		}
	}
	//#endregion betting

	//#region transactions
	trackDepositInitiated(paymentMethod: string, amount: number) {
		const data: DataType = {
			category: "Transactions",
			payment_method: paymentMethod,
			amount: amount.toString(),
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.DepositInitiated,
			data
		);
		if (this.userData) {
			this.xtremePushProvider.trackEvent(
				UserActionTypeEnum.DepositInitiated,
				data
			);
		}
	}

	trackDepositCompleted(paymentMethod: string, amount: number) {
		const data: DataType = {
			category: "Transactions",
			payment_method: paymentMethod,
			amount: amount.toString(),
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.DepositCompleted,
			data
		);

		if (this.userData) {
			this.xtremePushProvider.trackEvent(
				UserActionTypeEnum.DepositCompleted,
				data
			);
		}

		this.bingUETProvider?.trackEvent(
			UserActionTypeEnum.DepositCompleted,
			data
		);
		this.metaPixelProvider?.trackEvent(
			UserActionTypeEnum.DepositCompleted,
			data
		);
	}

	trackFirstDeposit() {
		const data = {
			category: "Transactions",
			page_location: `${window.location.origin}${window.location.pathname}`,
		} as DataType;

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.FirstDeposit,
			data
		);
	}

	trackWithdrawalInitiated(paymentMethod: string, amount: string) {
		const data: DataType = {
			category: "Transactions",
			payment_method: paymentMethod,
			amount: amount,
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.WithdrawalInitiated,
			data
		);

		if (this.userData) {
			this.xtremePushProvider.trackEvent(
				UserActionTypeEnum.WithdrawalInitiated,
				data
			);
		}
	}

	trackWithdrawalCompleted(paymentMethod: string, amount: string) {
		const data: DataType = {
			category: "Transactions",
			payment_method: paymentMethod,
			amount: amount,
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.WithdrawalCompleted,
			data
		);

		if (this.userData) {
			this.xtremePushProvider.trackEvent(
				UserActionTypeEnum.WithdrawalCompleted,
				data
			);
		}
	}
	//#endregion

	//#region authorization

	async trackIsUserLogged(response: LoginResponseUser) {
		this.setUserData(response);

		if (this.userData) {
			this.googleAnalyticsProvider.setUserId(this.userData);
		}

		if (this.userData) {
			if (
				this.xtremePushProvider instanceof CapacitorXtremePushProvider
			) {
				await this.xtremePushProvider.setUserId(this.userData);
			} else {
				this.xtremePushProvider.setUserId(this.userData);
			}
		}
	}

	async trackLoginSuccess(response: LoginResponseUser) {
		const data: DataType = {
			category: "Authorization",
		};

		this.setUserData(response);

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.LoginSuccess,
			data
		);

		if (this.userData) {
			this.googleAnalyticsProvider.setUserId(this.userData);
		}

		this.xtremePushProvider.trackEvent(
			UserActionTypeEnum.LoginSuccess,
			data
		);

		if (this.userData) {
			if (
				this.xtremePushProvider instanceof CapacitorXtremePushProvider
			) {
				await this.xtremePushProvider.setUserId(this.userData);
			} else {
				this.xtremePushProvider.setUserId(this.userData);
			}
		}
	}

	trackLogout() {
		const data: DataType = {
			category: "Authorization",
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.Logout,
			data
		);

		this.xtremePushProvider.trackEvent(UserActionTypeEnum.Logout, data);

		this.userData = null;
	}

	trackRegistrationStarted(type: string) {
		// type: normal, bankId, mitId, terminal, partner
		const data: DataType = {
			category: "Authorization",
			registration_type: type,
		};

		// always first step when user starts registration
		this.trackRegistrationProcess(1);

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.RegistrationStarted,
			data
		);
	}

	trackRegistrationProcess(step: number) {
		const stepToString = (): string => {
			switch (step) {
				case 1:
					return RegistrationStepsEnum.FirstStep;
				case 2:
					return RegistrationStepsEnum.SecondStep;
				case 3:
					return RegistrationStepsEnum.RegistrationSuccessful;
				default:
					return "";
			}
		};

		const data: DataType = {
			category: "Authorization",
			registration_step: stepToString(),
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.RegistrationProcess,
			data
		);
	}

	trackRegistrationFormCompleted() {
		const data: DataType = {
			category: "Authorization",
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.RegistrationFormCompleted,
			data
		);
	}

	trackFirstLogin() {
		const data: FirstLoginModel = {
			category: "Authorization",
			user_type: App.state.rootStore.userAuthStore.isInternetUser
				? UserTypeEnum.Online
				: UserTypeEnum.Retail,
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.FirstLogin,
			data
		);

		this.bingUETProvider?.trackEvent(UserActionTypeEnum.FirstLogin, data);
		this.metaPixelProvider?.trackEvent(UserActionTypeEnum.FirstLogin, data);
	}

	trackRegistrationCompleted() {
		const data: DataType = {
			category: "Authorization",
			registration_step: RegistrationStepsEnum.RegistrationSuccessful,
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.RegistrationProcess,
			data
		);
	}

	trackRegistrationCampaignSubmit(registrationType: string) {
		if (!getRegistrationCampaign()) {
			return;
		}

		const data: RegistrationCampaignSubmitModel = {
			category: "Authorization",
			registration_type: registrationType,
			...this.getRegistrationSessionMeta(),
		};

		if (this.hasRegistrationTrackingConsent()) {
			this.googleAnalyticsProvider.trackEvent(
				UserActionTypeEnum.RegistrationCampaignSubmit,
				data
			);
		}
	}

	trackRegistrationInteraction(
		label: RegistrationInteractionTypeEnum,
		meta?: { field_name?: string }
	) {
		if (!this.hasRegistrationTrackingConsent()) {
			return;
		}

		const data: RegistrationInteractionModel = {
			category: "Authorization",
			interaction_label: label,
			...this.getRegistrationSessionMeta(),
			...meta,
		};

		this.googleAnalyticsProvider.trackEvent(
			UserActionTypeEnum.RegistrationInteraction,
			data
		);
	}

	private hasRegistrationTrackingConsent(): boolean {
		if (isCapacitorPlatform()) {
			return true;
		}

		return isAnalyticsConsentSatisfied(
			GA_REGISTRATION_TRACKING_REQUIRES_CONSENT,
			"analytics"
		);
	}

	private getRegistrationSessionMeta() {
		const sessionId = getRegistrationSessionId();
		const campaign = getRegistrationCampaign();

		return {
			...(sessionId ? { registration_session_id: sessionId } : {}),
			...(campaign?.campaignId
				? { campaign_id: campaign.campaignId }
				: {}),
			...(campaign?.recipientId
				? { recipient_id: campaign.recipientId }
				: {}),
		};
	}

	//#endregion authorization

	//#region engagement
	// can't send email as param because google analytics redacts email values, email will be stored but as (redacted)
	trackNewsletterSubscribed(email: string) {
		const data: DataType = {
			category: "Engagement",
			email: email,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.NewsLetterSubscribed,
			data
		);

		if (this.userData) {
			this.xtremePushProvider.trackEvent(
				EngagementTrackingEnum.NewsLetterSubscribed,
				data
			);
		}
	}

	// currently on button click it sends ga parameters, but can't test because we don't have live streaming on dev
	trackLiveStreamingViewed(
		eventId: string,
		teamOneAbrv: string,
		teamTwoAbrv: string,
		tournamentName: string,
		sportAbrv: string
	) {
		const data: DataType = {
			category: "Engagement",
			event_id: eventId,
			team_one_abrv: teamOneAbrv,
			team_two_abrv: teamTwoAbrv,
			tournament_abrv: tournamentName,
			sport_type_abrv: sportAbrv,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.LiveStreamingViewed,
			data
		);
	}

	trackSocialMediaShared(shareType: string, betSlipId: string) {
		const data: DataType = {
			category: "Engagement",
			share_type: shareType,
			bet_id: betSlipId,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.SocialMediaShared,
			data
		);

		if (this.userData) {
			this.xtremePushProvider.trackEvent(
				EngagementTrackingEnum.SocialMediaShared,
				data
			);
		}
	}

	trackTournamentFavorited(
		tournamentAbrv: string,
		tournamentId: string,
		sportAbrv: string = ""
	) {
		const data: DataType = {
			category: "Engagement",
			tournament_abrv: tournamentAbrv,
			tournament_id: tournamentId,
			sport_type_abrv: sportAbrv,
		};
		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.TournamentFavorited,
			data
		);

		if (this.userData) {
			this.xtremePushProvider.trackEvent(
				EngagementTrackingEnum.TournamentFavorited,
				data
			);
		}
	}

	trackEventFavorited(
		eventId: string,
		teamOneAbrv: string,
		teamTwoAbrv: string,
		tournamentAbrv: string,
		sportAbrv: string
	) {
		const data: DataType = {
			category: "Engagement",
			event_id: eventId,
			team_one_abrv: teamOneAbrv,
			team_two_abrv: teamTwoAbrv,
			tournament_abrv: tournamentAbrv,
			sport_type_abrv: sportAbrv,
		};
		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.EventFavorited,
			data
		);

		if (this.userData) {
			this.xtremePushProvider.trackEvent(
				EngagementTrackingEnum.EventFavorited,
				data
			);
		}
	}

	trackBettingTypeFavorited(bettingTypeName: string) {
		const data: DataType = {
			category: "Engagement",
			betting_type: bettingTypeName,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.BettingTypeFavorited,
			data
		);

		if (this.userData) {
			this.xtremePushProvider.trackEvent(
				EngagementTrackingEnum.BettingTypeFavorited,
				data
			);
		}
	}

	trackSportInteraction(category: string, label?: string) {
		const data: DataType = {
			category: "Engagement",
			interaction_category: category,
			interaction_label: label,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.SportInteraction,
			data
		);
	}
	//#endregion engagement

	//#region events
	trackMatchViewed(
		matchId: string,
		sportAbrv: string,
		teamOneAbrv: string,
		teamTwoAbrv: string,
		tournamentAbrv: string
	) {
		const data: DataType = {
			category: "Event",
			match_id: matchId,
			sport_type_abrv: sportAbrv,
			team_one_abrv: teamOneAbrv,
			team_two_abrv: teamTwoAbrv,
			tournament_abrv: tournamentAbrv,
		};
		this.googleAnalyticsProvider.trackEvent(
			EventTrackingEnum.MatchViewed,
			data
		);
	}
	//#endregion events

	//#region casino

	trackCasinoInteraction(type: string, gameAbrv: string, provider: string) {
		const data: DataType = {
			category: "Casino",
			interaction_category: type,
			casino_game_abrv: gameAbrv,
			casino_provider: provider,
		};

		this.googleAnalyticsProvider.trackEvent(
			EventTrackingEnum.CasinoInteraction,
			data
		);
	}
	//#endregion casino

	//#region interactions

	trackQuickActionInteraction(type: string) {
		const data: DataType = {
			category: "Engagement",
			quick_action_type: type,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.QuickActionInteraction,
			data
		);
	}

	trackMarketingInteraction(type: string) {
		const data: DataType = {
			category: "Engagement",
			marketing_interaction_type: type,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.MarketingInteraction,
			data
		);
	}

	trackAdditionalOfferInteraction(
		type: string,
		sportAbrv?: string,
		teamOneAbrv?: string,
		teamTwoAbrv?: string
	) {
		const data: DataType = {
			category: "Offer",
			additional_offer_interaction_type: type,
			event_abrv: [sportAbrv, teamOneAbrv, teamTwoAbrv]
				.filter(Boolean)
				.join("-"),
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.AdditionalOfferInteraction,
			data
		);
	}

	trackLiveSideNavigationInteraction(type: string) {
		const data: DataType = {
			category: "Offer",
			live_side_nav_interaction_type: type,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.LiveSideNavigationInteraction,
			data
		);
	}

	trackHomePageOfferTabsInteraction(type: string, tabLabel: string) {
		const data: DataType = {
			category: "Offer",
			homepage_offer_tab_interaction_type: type,
			tab_label: tabLabel,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.HomepageOfferTabInteraction,
			data
		);
	}

	trackTournamentSwitcherInteraction(direction: string) {
		const data: DataType = {
			category: "Offer",
			tournament_switcher_direction: direction,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.TournamentSwitcherInteraction,
			data
		);
	}

	trackEventSwitcherInteraction(direction: string) {
		const data: DataType = {
			category: "Offer",
			event_switcher_direction: direction,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.EventSwitcherInteraction,
			data
		);
	}

	trackLiveEventSwitcherInteraction(direction: string) {
		const data: DataType = {
			category: "Offer",
			live_event_switcher_direction: direction,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.LiveEventSwitcherInteraction,
			data
		);
	}

	trackOpenChatInteraction() {
		const data: DataType = {
			category: "Chat",
			label: "chat-open",
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.ChatOpenInteraction,
			data
		);
	}

	trackQuickPlayInteraction(type: string) {
		const data: DataType = {
			category: "Quick Play",
			quick_play_interaction_type: type,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.QuickPlayInteraction,
			data
		);
	}

	trackBetBuilderInteraction(type: string) {
		const data: DataType = {
			category: "Betting",
			bet_builder_interaction_type: type,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.BetBuilderInteraction,
			data
		);
	}

	trackBetSpotlightInteraction(type: string) {
		const data: DataType = {
			category: "Bet Spotlight",
			bet_spotlight_interaction_type: type,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.BetSpotlightInteraction,
			data
		);
	}

	trackSportStatisticsInteractionTracking(type: string) {
		const data: DataType = {
			category: "Statistics",
			sport_statistics_interaction_type: type,
		};

		this.googleAnalyticsProvider.trackEvent(
			EngagementTrackingEnum.SportStatisticsInteraction,
			data
		);
	}

	trackModalInteraction(location: string) {
		const data: DataType = {
			category: "Betting",
		};

		this.googleAnalyticsProvider.trackEvent(
			location === "bb-picks"
				? EngagementTrackingEnum.BetBuilderModalOpen
				: EngagementTrackingEnum.MostPlayedModalOpen,
			data
		);
	}

	trackModalSwitcher(location: string) {
		const data: DataType = {
			category: "Betting",
		};

		this.googleAnalyticsProvider.trackEvent(
			location === "bb-picks"
				? EngagementTrackingEnum.BetBuilderModalSwitcherInteraction
				: EngagementTrackingEnum.MostPlayedModalSwitcherInteraction,
			data
		);
	}

	trackUseTicketEvent(location: string) {
		const data: DataType = {
			category: "Betting",
		};

		this.googleAnalyticsProvider.trackEvent(
			location === "bb-picks"
				? EventTrackingEnum.UseBetBuilderPick
				: EventTrackingEnum.UseMostPlayedTicket,
			data
		);
	}
	//#endregion interactions

	//#region search interactions

	trackSearchInputClick() {
		const data: DataType = {
			category: "Search",
		};

		this.googleAnalyticsProvider.trackEvent(
			SearchTrackingEnum.SearchButtonClicked,
			data
		);
	}

	trackSearchTerm(term: string) {
		const data: DataType = {
			category: "Search",
			search_term: term,
		};

		if (term !== "") {
			this.googleAnalyticsProvider.trackEvent(
				SearchTrackingEnum.SearchTermTrack,
				data
			);
		}
	}

	trackSearchType(type: SearchAction, term: string) {
		const data: DataType = {
			category: "Search",
		};

		let eventName: string = "";

		switch (type) {
			case SearchAction.SEARCH:
				eventName = SearchTrackingEnum.FreeSearchTrack;
				this.trackSearchTerm(term);
				break;
			case SearchAction.PICK_FIRST:
				eventName = SearchTrackingEnum.FreeSearchTrack;
				this.trackSearchTerm(term);
				break;
			case SearchAction.PICK:
				eventName = SearchTrackingEnum.TournamentSearchTrack;
				break;
			case SearchAction.EVENT_PICK:
				eventName = SearchTrackingEnum.EventSearchTrack;
				break;
			default:
				return;
		}

		this.googleAnalyticsProvider.trackEvent(eventName, data);
	}
	//#endregion search interactions

	//#region live event types tracking

	trackLiveEventInteraction(type: LiveEventTypeTrackingEnum) {
		const data: DataType = {
			category: "Live",
		};

		let eventName: string = "";

		switch (type) {
			case LiveEventTypeTrackingEnum.LiveMultiEventAdded:
				eventName = LiveEventTypeTrackingEnum.LiveMultiEventAdded;
				break;
			case LiveEventTypeTrackingEnum.LiveAdditionalOfferOpened:
				eventName = LiveEventTypeTrackingEnum.LiveAdditionalOfferOpened;
				break;
			case LiveEventTypeTrackingEnum.LiveEventPageOpened:
				eventName = LiveEventTypeTrackingEnum.LiveEventPageOpened;
				break;
			default:
				return;
		}

		this.googleAnalyticsProvider.trackEvent(eventName, data);
	}

	//#endregion live event types tracking
}

const analyticsTracker = new AnalyticsTracker();

export { analyticsTracker };
